/** Primary gameplay scene — manages the player, enemies, combat, and autosaving during a run. */
import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { MAP_HEIGHT_PX, MAP_WIDTH_PX, SF0_X, SF0_Y, GAME_WIDTH, CURRENT_ZONE } from '../utils/Constants';
import { crispText } from '../ui/CrispUtil';
import { Font, Hex } from '../ui/Theme';
import { fadeIn } from '../utils/SceneTransitions';
import { magnitude, tileToWorld } from '../utils/Math';
import { SaveSystem, RunState } from '../systems/SaveSystem';
import { CombatSystem } from '../systems/CombatSystem';
import { HitstopSystem } from '../systems/HitstopSystem';
import { ScreenEffects } from '../systems/ScreenEffects';
import { EnemyManager } from '../systems/EnemyManager';
import { ConfigLoader } from '../systems/ConfigLoader';
import { KnockbackSystem } from '../systems/KnockbackSystem';
import { SimulationBridge } from '../simulation/SimulationBridge';
import { WallPhysicsSystem } from '../systems/WallPhysicsSystem';
import { DamageNumbers } from '../systems/DamageNumbers';
import { KeyBindings, GameAction } from '../systems/KeyBindings';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugContextMenu } from '../debug/DebugContextMenu';
import { DebugLog } from '../debug/DebugLog';
import { DebugOverlayManager } from '../debug/DebugOverlayManager';
import { ProjectilePool } from '../systems/ProjectilePool';
import { EnemyBuffManager } from '../systems/EnemyBuffManager';
import { UpgradeEffectResolver } from '../systems/UpgradeEffectResolver';
import { StatusEffectSystem } from '../systems/StatusEffectSystem';
import { ZoneSystem } from '../systems/ZoneSystem';
import { SummonSystem } from '../systems/SummonSystem';
import { DeathTriggerSystem } from '../systems/DeathTriggerSystem';
import { ChainTargetingSystem } from '../systems/ChainTargetingSystem';
import { CooldownHookSystem } from '../systems/CooldownHookSystem';
import { ChargeSystem } from '../systems/ChargeSystem';
import { StationaryTracker } from '../systems/StationaryTracker';
import { TerrainConstructionSystem } from '../systems/TerrainConstructionSystem';
import { PlayerProjectilePool } from '../systems/PlayerProjectilePool';
import { AstralTeleportSystem } from '../systems/AstralTeleportSystem';
import { ForesightRenderSystem } from '../systems/ForesightRenderSystem';
import { GravityPullSystem } from '../systems/GravityPullSystem';
import { OrbitalDebrisSystem } from '../systems/OrbitalDebrisSystem';
import { TemporalEchoStateSystem } from '../systems/TemporalEchoStateSystem';
import { AstralProjectionStateSystem } from '../systems/AstralProjectionStateSystem';
import statusEffectsJson from '../data/status-effects.json';
import { SkillFactory } from './GameSceneSkillFactory';
import { SkillEffectDeclarationEngine } from '../systems/SkillEffectDeclarationEngine';
import { computeFrameConcealmentState } from './GameSceneHideState';
import { wireEnemySubsystems } from './GameSceneEnemyWiring';
import { wireCrawlOverlay } from './GameSceneCrawlWiring';
import { resolvePlayerWallOverlap, getPlayerBodyGeometry } from './GameScenePlayerPhysics';
import { CombatHitResolver } from '../systems/CombatHitResolver';
import { SkillVFXOrchestrator } from '../vfx/SkillVFXOrchestrator';
import { StatusVFXOrchestrator } from '../vfx/StatusVFXOrchestrator';
import { TraceRenderer } from '../systems/TraceRenderer';
import { MarkRenderer } from '../systems/MarkRenderer';
import { DeathParticleEffect } from '../effects/DeathParticleEffect';
import { StatusEffectEvent } from '../systems/StatusEffectTracker';
import { HealthComponent } from '../components/HealthComponent';
import { DeathIrisEffect } from '../effects/DeathIrisEffect';
import { DeathSequence } from '../systems/DeathSequence';
import { BuildingSystem } from '../systems/BuildingSystem';
import { ForkChoiceScene } from './ForkChoiceScene';
import { bootstrapScene, setupCamera, setupCollisionTiles, findSpawnTile } from './GameSceneBootstrap';
import type { Position } from '../utils/Types';
import type { SlotState } from '../ui/SkillSlotsHUD';

const AUTO_SAVE_INTERVAL = 2000;

interface GameData {
  saveName: string;
  isNew: boolean;
}

/**
 * Orchestrates a single run: tilemap rendering, player and enemy updates, combat resolution,
 * skill effects, HUD event emission, and periodic save writes.
 */
export class GameScene extends Phaser.Scene {
  private map!: Phaser.Tilemaps.Tilemap;
  private groundLayer!: Phaser.Tilemaps.TilemapLayer;
  player!: Player;
  private saveSystem!: SaveSystem;
  private saveName!: string;
  private timeSinceLastSave = 0;
  private escKey!: Phaser.Input.Keyboard.Key;

  // Combat systems
  private combatSystem!: CombatSystem;
  private hitstopSystem!: HitstopSystem;
  private screenEffects!: ScreenEffects;
  enemyManager!: EnemyManager;
  private knockbackSystem!: KnockbackSystem;
  private debugOverlay!: DebugOverlayManager;
  private damageNumbers!: DamageNumbers;
  private projectilePool!: ProjectilePool;
  private buffManager!: EnemyBuffManager;
  private wallPhysics!: WallPhysicsSystem;
  private upgradeResolver!: UpgradeEffectResolver;
  private traceRenderer!: TraceRenderer;
  private markRenderer!: MarkRenderer;
  private deathState: 'alive' | 'dying' | 'dead' | 'respawning' = 'alive';
  /** When true, suppress ForkChoiceScene launch — headless sim has no UI to dismiss it. */
  private _simMode = false;
  private deathParticles!: DeathParticleEffect;
  private deathIris!: DeathIrisEffect;
  private getDanger!: () => number;
  private combatHitResolver!: CombatHitResolver;
  private deathSequence!: DeathSequence;
  private buildingSystem!: BuildingSystem;

  // Shared attunement systems (ADR-064)
  private statusEffectSystem!: StatusEffectSystem;
  private zoneSystem!: ZoneSystem;
  private summonSystem!: SummonSystem;
  private deathTriggerSystem!: DeathTriggerSystem;
  private chainTargetingSystem!: ChainTargetingSystem;
  private cooldownHookSystem!: CooldownHookSystem;
  private chargeSystem!: ChargeSystem;
  private stationaryTracker!: StationaryTracker;
  private terrainConstructionSystem!: TerrainConstructionSystem;
  private playerProjectilePool!: PlayerProjectilePool;
  // Astral-specific systems
  private astralTeleportSystem!: AstralTeleportSystem;
  private foresightRenderSystem!: ForesightRenderSystem;
  private gravityPullSystem!: GravityPullSystem;
  private orbitalDebrisSystem!: OrbitalDebrisSystem;
  private temporalEchoStateSystem!: TemporalEchoStateSystem;
  private astralProjectionStateSystem!: AstralProjectionStateSystem;
  private skillVFXOrchestrator!: SkillVFXOrchestrator;
  private statusVFXOrchestrator!: StatusVFXOrchestrator;
  /** Tracks which enemy instanceTags have been registered with StatusVFXOrchestrator. */
  private statusVfxRegisteredEnemies: Set<string> = new Set();
  private skillEffectDeclarationEngine!: SkillEffectDeclarationEngine;
  /** Exposed for SimulationBridge AI adapter query testing. */
  skillFactory!: SkillFactory;

  constructor() {
    super({ key: 'GameScene' });
  }

  /** Store save name and instantiate SaveSystem before create runs. */
  init(data: GameData): void {
    this.saveName = data.saveName;
    this.saveSystem = new SaveSystem();
  }

  /** Build the tilemap, spawn the player and enemies, wire all systems, and launch UIScene. */
  create(): void {
    // ── Reset ──────────────────────────────────────────────────────────
    // Phaser reuses scene instances — constructor runs once, create() runs every start()
    this.deathState = 'alive';
    this.deathParticles = new DeathParticleEffect(this);
    this.deathIris = new DeathIrisEffect(this);

    // ── World bootstrap ───────────────────────────────────────────────
    const boot = bootstrapScene(this, this.saveSystem, this.saveName, null);
    this.map = boot.map;
    this.groundLayer = boot.groundLayer;
    this.wallPhysics = boot.wallPhysics;
    this.buildingSystem = boot.buildingSystem;
    this.projectilePool = boot.projectilePool;
    const sectorConfig = boot.sectorConfig;
    const existingSave = this.saveSystem.loadRun(this.saveName);
    let spawnX = boot.spawnX;
    let spawnY = boot.spawnY;

    // ── Player ────────────────────────────────────────────────────────
    this.player = new Player(this, spawnX, spawnY);

    // Validate spawn — spiral outward if body overlaps walls
    const geo = getPlayerBodyGeometry(this.player);
    if (geo) {
      DebugLog.log('physics', `SPAWN-CHECK sprite=(${spawnX},${spawnY}) geo={w:${geo.width.toFixed(1)},h:${geo.height.toFixed(1)},offX:${geo.offsetX.toFixed(1)},offY:${geo.offsetY.toFixed(1)},dOX:${geo.displayOriginX.toFixed(1)},dOY:${geo.displayOriginY.toFixed(1)}} body=(${(spawnX - geo.displayOriginX + geo.offsetX).toFixed(1)},${(spawnY - geo.displayOriginY + geo.offsetY).toFixed(1)}) clear=${this.wallPhysics.isBodyPositionClear(spawnX, spawnY, geo)}`);
      if (!this.wallPhysics.isBodyPositionClear(spawnX, spawnY, geo)) {
        const safe = this.wallPhysics.findNearestClearPosition(spawnX, spawnY, geo);
        if (safe) {
          DebugLog.log('physics', `SPAWN-TELEPORT from (${spawnX},${spawnY}) to (${safe.x},${safe.y})`);
          this.player.body.reset(safe.x, safe.y);
        }
      }
    }

    // Restore persisted skill progress and attunements
    const charData = this.saveSystem.loadCharacter(this.saveName);
    if (Object.keys(charData.skillProgress).length > 0) {
      this.player.skillProgression.importProgress(charData.skillProgress);
    }
    if (charData.hotbar) {
      this.player.attunement.importHotbar(charData.hotbar);
    } else if (charData.attunedSkills.length > 0) {
      this.player.attunement.importAttuned(charData.attunedSkills);
    }
    if (charData.selectedTrees && charData.selectedTrees.length > 0) {
      this.player.attunement.importSelectedTrees(charData.selectedTrees);
    }

    // ── World physics (needs player sprite) ───────────────────────────
    this.physics.world.setBounds(0, 0, MAP_WIDTH_PX, MAP_HEIGHT_PX);
    setupCamera(this, this.groundLayer, this.player.sprite);
    if (sectorConfig?.buildings) {
      for (const b of sectorConfig.buildings) {
        this.buildingSystem.addBuilding(b, this.player.sprite);
      }
    }
    setupCollisionTiles(this, this.groundLayer, this.player.sprite);

    // ── Combat core ───────────────────────────────────────────────────
    this.hitstopSystem = new HitstopSystem();
    this.screenEffects = new ScreenEffects(this);
    this.combatSystem = new CombatSystem(this, this.hitstopSystem, this.screenEffects);
    this.upgradeResolver = new UpgradeEffectResolver(this.player.skillProgression, this.player.events);
    this.knockbackSystem = new KnockbackSystem(this, this.groundLayer, () => this.enemyManager.all);
    this.damageNumbers = new DamageNumbers(this);
    this.debugOverlay = new DebugOverlayManager(this, this.groundLayer);

    // Wire upgrade resolution into combat and movement
    this.player.combat.resolveRecoveryMs = (attackType: string, baseRecoveryMs: number): number => {
      const xpSkillId = ConfigLoader.getAttackConfig(attackType)?.skillId ?? 'punch';
      return this.upgradeResolver.getResolvedRecoveryMs(xpSkillId, baseRecoveryMs);
    };
    this.player.resolveStealthSpeed = (baseMult: number): number => {
      return this.upgradeResolver.getMovementSpeedMultiplier('stealth', baseMult);
    };

    // Apply passive max HP bonus (e.g. Thick Skin) — must run after both skill import and resolver construction
    this.applyMaxHpPassive();

    // ── Enemies ───────────────────────────────────────────────────────
    const enemies = wireEnemySubsystems({
      scene: this, groundLayer: this.groundLayer, playerSprite: this.player.sprite,
      buildingSystem: this.buildingSystem, projectilePool: this.projectilePool,
      sectorConfig, spawnX, spawnY,
    });
    this.enemyManager = enemies.enemyManager;
    this.buffManager = enemies.buffManager;

    // ── Shared attunement systems (ADR-064) ─────────────────────────────
    this.statusEffectSystem = new StatusEffectSystem();
    this.statusEffectSystem.registerFromJson(statusEffectsJson as Parameters<StatusEffectSystem['registerFromJson']>[0]);
    this.player.statusEffectTracker = this.statusEffectSystem.createTracker();
    this.enemyManager.setStatusEffectSystem(this.statusEffectSystem);
    this.zoneSystem = new ZoneSystem();
    this.summonSystem = new SummonSystem();
    this.deathTriggerSystem = new DeathTriggerSystem();
    this.chainTargetingSystem = new ChainTargetingSystem();
    this.cooldownHookSystem = new CooldownHookSystem();
    this.chargeSystem = new ChargeSystem();
    this.stationaryTracker = new StationaryTracker();
    this.terrainConstructionSystem = new TerrainConstructionSystem();
    this.playerProjectilePool = new PlayerProjectilePool();
    this.astralTeleportSystem = new AstralTeleportSystem();
    this.foresightRenderSystem = new ForesightRenderSystem();
    this.gravityPullSystem = new GravityPullSystem();
    this.orbitalDebrisSystem = new OrbitalDebrisSystem();
    this.temporalEchoStateSystem = new TemporalEchoStateSystem({
      setPosition: (x, y) => { this.player.body.reset(x, y); },
      setHp: (hp) => { this.player.health.setHp(hp); },
    });
    this.astralProjectionStateSystem = new AstralProjectionStateSystem({
      spawnProjection: () => { /* Phaser rendering deferred to vfx-pass */ },
      despawnProjection: () => { /* Phaser rendering deferred to vfx-pass */ },
      lockBody: () => { this.player.body.enable = false; },
      unlockBody: () => { this.player.body.enable = true; },
    });

    // ── Skill effect declarations engine (ADR-087) ────────────────────
    this.skillEffectDeclarationEngine = new SkillEffectDeclarationEngine();

    // ── Skills & passives ─────────────────────────────────────────────
    this.skillFactory = new SkillFactory({
      player: this.player, enemyManager: this.enemyManager,
      upgradeResolver: this.upgradeResolver, cameras: this.cameras,
      statusEffectSystem: this.statusEffectSystem,
      zoneSystem: this.zoneSystem,
      summonSystem: this.summonSystem,
      deathTriggerSystem: this.deathTriggerSystem,
      chainTargetingSystem: this.chainTargetingSystem,
      cooldownHookSystem: this.cooldownHookSystem,
      chargeSystem: this.chargeSystem,
      stationaryTracker: this.stationaryTracker,
      terrainConstructionSystem: this.terrainConstructionSystem,
      playerProjectilePool: this.playerProjectilePool,
      knockbackSystem: this.knockbackSystem,
      astralTeleportSystem: this.astralTeleportSystem,
      foresightRenderSystem: this.foresightRenderSystem,
      gravityPullSystem: this.gravityPullSystem,
      orbitalDebrisSystem: this.orbitalDebrisSystem,
      temporalEchoStateSystem: this.temporalEchoStateSystem,
      astralProjectionStateSystem: this.astralProjectionStateSystem,
      groundLayer: this.groundLayer,
      declarationEngine: this.skillEffectDeclarationEngine,
      damageNumbers: this.damageNumbers,
      pushCrawl: (text: string, color: string) => this.events.emit('crawl-push', { text, color }),
    });
    this.getDanger = this.skillFactory.getDanger;

    // Wire stealth corpse vanish (Ghostwalk passive + stealthAttack flag)
    this.enemyManager.setStealthCorpseVanish(() =>
      this.player.combat.stealthAttack &&
      this.player.skillProgression.isUnlocked('specter_corpse_vanish'),
    );

    this.traceRenderer = new TraceRenderer(this);
    this.markRenderer = new MarkRenderer(this);
    this.syncSpecterPassives();

    // ── Combat resolution (needs enemies + skills + combat core) ─────
    this.combatHitResolver = new CombatHitResolver({
      player: this.player,
      enemyManager: this.enemyManager,
      combatSystem: this.combatSystem,
      hitstopSystem: this.hitstopSystem,
      knockbackSystem: this.knockbackSystem,
      upgradeResolver: this.upgradeResolver,
      damageNumbers: this.damageNumbers,
      pushCrawl: (text: string, color: string) => this.events.emit('crawl-push', { text, color }),
      screenEffects: this.screenEffects,
      buffManager: this.buffManager,
      getDanger: this.getDanger,
      statusEffectSystem: this.statusEffectSystem,
      zoneSystem: this.zoneSystem,
      chainTargetingSystem: this.chainTargetingSystem,
      deathTriggerSystem: this.deathTriggerSystem,
    });
    this.combatHitResolver.setupProjectileOverlap(this, this.projectilePool);

    // ── Events ────────────────────────────────────────────────────────
    // Forward player events to scene-level events for UIScene
    this.player.events.on('health-changed', (current: number, max: number) => {
      this.events.emit('player-health-changed', current, max);
    });
    this.player.events.on('stamina-changed', (current: number, max: number) => {
      this.events.emit('player-stamina-changed', current, max);
    });
    this.player.events.on('dash-changed', (charges: number, max: number, refillProgress: number) => {
      this.events.emit('player-dash-changed', charges, max, refillProgress);
    });
    this.player.events.on('entity-died', () => this.onPlayerDeath());
    this.player.events.on('tree-selection-changed', (data: { selectedTrees: string[] }) => {
      this.events.emit('tree-selection-changed', data);
    });
    this.player.events.on('node-unlocked', (data: { treeName: string; nodeId: string; nodeType: string }) => {
      this.syncSpecterPassives();
      this.applyMaxHpPassive();
      if (this._simMode) return; // Sim mode: no fork-choice UI to dismiss
      ForkChoiceScene.enqueue(data);
      if (!this.scene.isActive('ForkChoiceScene')) {
        const next = ForkChoiceScene.dequeue()!;
        this.scene.pause();
        this.scene.launch('ForkChoiceScene', { mode: 'attained' as const, ...next });
      }
    });
    this.player.events.on('fork-choice-available', (data: { treeName: string; forkId: string; options: string[] }) => {
      if (this._simMode) return; // Sim mode: forks auto-chosen by SimulationBridge
      this.scene.pause();
      this.scene.launch('ForkChoiceScene', { mode: 'fork' as const, ...data });
    });

    // Re-offer any pending forks from a previous session (e.g. player reloaded
    // mid-popup before making a choice). Delayed one frame so the scene is
    // fully running before we pause it for ForkChoiceScene.
    this.time.delayedCall(0, () => {
      for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
        const pending = this.player.skillProgression.getPendingForks(treeName);
        if (pending.length > 0) {
          this.player.events.emit('fork-choice-available', pending[0]);
          return; // One at a time — next fork fires after this one is resolved
        }
      }
    });

    wireCrawlOverlay(this.player, this.events, this.damageNumbers);

    // ── VFX orchestration ─────────────────────────────────────────────
    this.skillVFXOrchestrator = new SkillVFXOrchestrator(this);
    this.skillVFXOrchestrator.wire(
      this.player.events,
      () => ({
        x: this.player.sprite.x,
        y: this.player.sprite.y,
        depth: this.player.sprite.depth,
      }),
      () => Math.atan2(this.player.facingDirection.y, this.player.facingDirection.x),
    );

    // Status effect VFX — polls each registered entity's StatusEffectTracker
    this.statusVFXOrchestrator = new StatusVFXOrchestrator(this);
    this.statusVfxRegisteredEnemies = new Set();
    // Register the player for status VFX (e.g. self-applied debuffs)
    this.statusVFXOrchestrator.registerEntity(
      'player',
      () => ({ x: this.player.sprite.x, y: this.player.sprite.y, depth: this.player.sprite.depth }),
      () => this.player.statusEffectTracker ?? null,
      () => ('setTint' in this.player.sprite ? this.player.sprite as Phaser.GameObjects.Sprite : null),
    );
    // Backfill any enemies already spawned before this point
    for (const enemy of this.enemyManager.all) {
      this.registerEnemyStatusVfx(enemy);
    }

    // ── Lifecycle ─────────────────────────────────────────────────────
    this.deathSequence = new DeathSequence({
      scene: this,
      player: this.player,
      enemyManager: this.enemyManager,
      knockbackSystem: this.knockbackSystem,
      screenEffects: this.screenEffects,
      deathParticles: this.deathParticles,
      deathIris: this.deathIris,
      wallPhysics: this.wallPhysics,
      saveSystem: this.saveSystem,
      saveName: this.saveName,
      getSpawnPoint: () => this.getSpawnPoint(),
      getPlayerBodyGeometry: () => getPlayerBodyGeometry(this.player),
      applyMaxHpPassive: () => this.applyMaxHpPassive(),
      onStateChange: (state) => { this.deathState = state; },
    });

    this.scene.launch('UIScene');

    if (!existingSave) {
      this.savePosition();
    }
    this.timeSinceLastSave = 0;

    if (this.input.keyboard) {
      const pauseBinding = KeyBindings.getBinding(GameAction.Pause);
      this.escKey = this.input.keyboard.addKey(pauseBinding.primary);
      this.escKey.on('down', () => {
        this.savePosition();
        this.scene.pause();
        this.scene.launch('PauseScene');
      });

      // Debug shortcut: B opens the fork choice popup (B again cycles through forks)
      this.input.keyboard.on('keydown-B', () => {
        if (this.scene.isActive('ForkChoiceScene')) return;
        this.scene.pause();
        this.scene.launch('ForkChoiceScene', {
          treeName: 'grit',
          forkId: 'grit_shared_passive_2',
          options: ['hurtmore', 'grapple', 'ironbody'],
        });
      });
    }

    this.events.on('shutdown', () => {
      this.savePosition();
      this.screenEffects.destroy();
      this.deathParticles.destroy();
      this.deathIris.destroy();
      this.knockbackSystem.clear();
      this.debugOverlay.destroy();
      this.damageNumbers.destroy();
      this.projectilePool.destroy();
      this.enemyManager.destroy();
      this.buildingSystem.destroy();
      // Shared attunement systems (ADR-064) — teardown to prevent leaks across run restarts
      this.summonSystem.destroyAll();
      this.astralTeleportSystem.destroy();
      this.foresightRenderSystem.destroy();
      this.gravityPullSystem.destroy();
      this.orbitalDebrisSystem.destroy();
      this.temporalEchoStateSystem.destroy();
      this.astralProjectionStateSystem.destroy();
      // Remaining shared systems — clear accumulated state to prevent leaks across run restarts
      this.zoneSystem.removeAll();
      this.terrainConstructionSystem.clear();
      this.chargeSystem.clear();
      this.playerProjectilePool.destroy();
      this.stationaryTracker.reset();
      // Deactivate all active skills BEFORE destroying VFX orchestrator — emits skill-deactivated
      // so the orchestrator can clean up sustain VFX while it's still alive to receive the event.
      this.player?.skillActivation?.deactivateAll();
      this.skillVFXOrchestrator.destroy();
      this.statusVFXOrchestrator.destroy();
      this.statusVfxRegisteredEnemies.clear();
    });

    // Debug context menu (right-click world manipulation)
    new DebugContextMenu(this, this.player, this.enemyManager, this.groundLayer);

    fadeIn(this);

    // Register simulation bridge for headless testing (only active when ?sim=1)
    const simBridge = SimulationBridge.getInstance();
    simBridge?.register(this);
    simBridge?.installObserverHooks(this.damageNumbers, this.statusVFXOrchestrator);

    if (DebugConfig.firstLoad && DebugConfig.enabled) {
      this.showDebugNotice();
    }
  }

  /** Run one game tick: update player, enemies, combat, skill HUD state, and autosave timer. */
  update(_time: number, delta: number): void {
    if (this.deathState === 'dead') return;

    // --- Systems that run during both 'alive' and 'dying' ---
    this.combatSystem.update();

    // Build concealment state — during death the player is "gone", enemies can't detect them
    const conceal = computeFrameConcealmentState(this.player, this.deathState, this.upgradeResolver);

    this.enemyManager.update(this.player.body.center.x, this.player.body.center.y, delta, conceal.playerHideState);
    this.knockbackSystem.update(delta);
    this.projectilePool.update(delta);

    // Tick status effect durations/DoT for player and all enemies
    const playerSEEvents = this.player.statusEffectTracker?.update(delta) ?? [];
    this.processDoTEvents(playerSEEvents, this.player.health, this.player.sprite.x, this.player.sprite.y, true);
    for (const enemy of this.enemyManager.all) {
      const enemySEEvents = enemy.statusEffectTracker?.update(delta) ?? [];
      const enemyTag = SimulationBridge.getInstance()?.getTagForEnemy(enemy) ?? enemy.instanceTag;
      this.processDoTEvents(enemySEEvents, enemy.healthComponent, enemy.sprite.x, enemy.sprite.y, false, enemy.getVulnerabilityMultiplier(), enemyTag);
    }

    // Shared attunement systems — tick zones, summons, terrain, etc. even during death
    // Event arrays collected for future consumption (CombatHitResolver integration, VFX)
    const _zoneEvents = this.zoneSystem.update(delta, this.enemyManager.all.map(e => ({ id: e.instanceTag, x: e.sprite.x, y: e.sprite.y })));
    const _summonEvents = this.summonSystem.update(delta);
    const _terrainEvents = this.terrainConstructionSystem.update(delta);
    const _projectileEvents = this.playerProjectilePool.update(delta);
    this.astralTeleportSystem.update(delta);
    this.foresightRenderSystem.update(delta);
    this.skillVFXOrchestrator.update(delta);
    this.syncStatusVfxEntities();
    this.statusVFXOrchestrator.update(delta);
    this.orbitalDebrisSystem.update(delta);
    this.temporalEchoStateSystem.update(delta);
    this.astralProjectionStateSystem.update(delta);

    // Trace + Marked passive updates (after enemy AI so justLostPlayer is set)
    this.traceRenderer.update(this.enemyManager.all, this.player.sprite.x, this.player.sprite.y, conceal.isConcealed);
    this.markRenderer.update(this.enemyManager.all, delta);

    // Building alpha fade (data-driven — handles all buildings)
    this.buildingSystem.update(this.player.sprite.x, this.player.body.bottom);

    // --- Player-only systems (skip during death) ---
    if (this.deathState === 'alive') {
      this.player.update(delta);

      this.combatHitResolver.checkPlayerAttackHits();
      this.combatHitResolver.checkEnemyAttackHits();

      // Safety net: if the player body overlaps any wall tile, push them out
      resolvePlayerWallOverlap(this.player, this.wallPhysics);

      // Feed movement distance to stealth effect for step countdown
      const vel = this.player.body.velocity;
      const movementPx = magnitude(vel.x, vel.y) * (delta / 1000);
      conceal.feedStealthMovement(movementPx);

      // Player-dependent shared systems
      this.stationaryTracker.update(delta, movementPx);
      this.chargeSystem.update(delta, movementPx, this.player.running, this.player.dash.isDashing);
      this.gravityPullSystem.update(
        delta,
        this.enemyManager.all.map(e => ({ id: e.instanceTag, x: e.sprite.x, y: e.sprite.y })),
        {
          setPosition: (entityId, x, y) => {
            const enemy = this.enemyManager.all.find(e => e.instanceTag === entityId);
            if (enemy) { enemy.sprite.setPosition(x, y); }
          },
        },
      );

      // Emit skill slot state for HUD (4 V4 hotbar slots)
      const slotStates: SlotState[] = [];
      for (let i = 0; i < ConfigLoader.v4Hotbar.slotCount; i++) {
        const skillId = this.player.attunement.getSkillAtSlot(i);
        const state = this.buildSlotState(skillId);
        if (skillId === 'stealth') {
          state.steps = conceal.stealthStepsRemaining;
        }
        slotStates.push(state);
      }
      this.events.emit('player-skill-slots', slotStates);

      // Visual feedback: dim player sprite when hidden/stealthed
      this.player.sprite.setAlpha(conceal.isInvisible ? 0.4 : 1);

      // Autosave timer
      this.timeSinceLastSave += delta;
      if (this.timeSinceLastSave >= AUTO_SAVE_INTERVAL) {
        this.savePosition();
        this.timeSinceLastSave = 0;
      }
    }

    // Remove enemies that died this frame (wall damage, combat, projectiles, any source)
    this.enemyManager.sweepDeadEnemies();

    // Debug overlays always update (so enemies remain visible during death linger)
    this.debugOverlay.update(this.enemyManager.all);
  }

  /** Build the HUD state for a single hotbar slot (active flag, cooldown progress). */
  private buildSlotState(skillId: string | null): SlotState {
    if (!skillId) return { skillId: null, active: false, cooldown: 0, totalCooldown: 0 };
    const effect = this.player.skillActivation.getEffect(skillId);
    return {
      skillId,
      active: effect?.isActive?.() ?? false,
      cooldown: effect?.cooldown ?? 0,
      totalCooldown: effect?.cooldownDuration ?? 0,
    };
  }

  /** Apply HP reduction for any dot_tick events returned by StatusEffectTracker.update(). */
  private processDoTEvents(
    events: StatusEffectEvent[],
    health: HealthComponent,
    worldX: number,
    worldY: number,
    isPlayer: boolean,
    damageMult: number = 1,
    targetTag: string = 'player',
  ): void {
    const bridge = SimulationBridge.getInstance();
    for (const ev of events) {
      if (ev.type !== 'dot_tick' || !ev.damage || ev.damage <= 0) continue;
      const damage = damageMult !== 1 ? Math.round(ev.damage * damageMult) : ev.damage;
      health.takeDamage(damage);
      this.damageNumbers.spawn(worldX, worldY - 8, damage, ev.damage, false, false, isPlayer);
      const label = isPlayer ? `${ev.defId} ticks you for ${damage}` : `${ev.defId} ticks for ${damage}`;
      this.events.emit('crawl-push', { text: label, color: '#cc88ff' });
      bridge?.recordDotTick({ timestamp: Date.now(), statusId: ev.defId, targetTag, damage });
    }
  }

  /** Persist the current run state (HP, position, progression) to localStorage via SaveSystem. */
  private savePosition(): void {
    const state: RunState = {
      hp: this.player.health.hp,
      maxHp: this.player.health.max,
      position: {
        x: this.player.sprite.x,
        y: this.player.sprite.y,
      },
      zone: CURRENT_ZONE,
      inventory: [],
    };
    this.saveSystem.saveRun(this.saveName, state);

    // Persist skill progress and attunements (per-character, survives death)
    this.saveSystem.saveCharacter(this.saveName, {
      skillProgress: this.player.skillProgression.exportProgress(),
      attunedSkills: this.player.attunement.exportAttuned(),
      hotbar: this.player.attunement.exportHotbar(),
      attunementCapacity: this.player.attunement.totalSlots,
      selectedTrees: this.player.attunement.exportSelectedTrees(),
      unlockedSkills: [],
      discoveredLore: [],
    });
  }

  /** Display a transient "DEBUG MODE ON" banner that fades in, holds, then fades out. */
  private showDebugNotice(): void {
    const notice = crispText(this, SF0_X + GAME_WIDTH / 2, SF0_Y + 40,
      'DEBUG MODE ON — all options enabled. Press ESC to configure.',
      { fontFamily: Font.family, fontSize: Font.sizeBody, color: Hex.debugBright },
    ).setOrigin(0.5).setScrollFactor(0).setDepth(950).setAlpha(0);

    this.tweens.add({
      targets: notice,
      alpha: 1,
      duration: 400,
      hold: 3000,
      yoyo: true,
      onComplete: () => notice.destroy(),
    });
  }

  /** Initiate the full death sequence: iris close, shatter particles, fade, respawn, and coalesce. */
  private onPlayerDeath(): void {
    this.deathSequence.execute();
  }

  /** Determine where the player respawns after death. Currently sector hub; will route to Hub scene later. */
  private getSpawnPoint(): Position {
    const sectorConfig = ConfigLoader.getSectorConfig(CURRENT_ZONE);
    if (sectorConfig) {
      return { x: sectorConfig.hubSpawn.x, y: sectorConfig.hubSpawn.y };
    }
    const tile = findSpawnTile(this.map, this.groundLayer);
    if (tile) {
      return { x: tileToWorld(tile.x), y: tileToWorld(tile.y) };
    }
    return { x: MAP_WIDTH_PX / 2, y: MAP_HEIGHT_PX / 2 };
  }

  /** Recalculate and apply the passive max HP multiplier (e.g. Thick Skin 2.5x). */
  applyMaxHpPassive(): void {
    const mult = this.upgradeResolver.getMaxHpMultiplier();
    const newMax = Math.round(ConfigLoader.combat.player.maxHp * mult);
    if (newMax !== this.player.health.max) {
      this.player.health.setMaxHp(newMax);
    }
  }

  /** Reads a typed field from a V4 node's effect block, returning fallback if missing. */
  private getNodeEffectField<T>(nodeId: string, field: string, fallback: T): T {
    const node = ConfigLoader.getV4Node(nodeId);
    if (node && 'effect' in node) {
      const effect = node.effect as Record<string, unknown>;
      if (field in effect) return effect[field] as T;
    }
    return fallback;
  }

  /** Check whether the Trace and Marked Specter passives are unlocked, and enable/disable their renderers accordingly. */
  private syncSpecterPassives(): void {
    const prog = this.player.skillProgression;

    // Trace: patrol route visualization while concealed
    if (prog.isUnlocked('specter_shared_passive_1')) {
      const lookahead = this.getNodeEffectField<number>('specter_shared_passive_1', 'patrolLookaheadSeconds', 3);
      this.traceRenderer.enable(lookahead);
    } else {
      this.traceRenderer.disable();
    }

    // Marked: tag enemies who lose sight of the player
    if (prog.isUnlocked('specter_shared_passive_2')) {
      const duration = this.getNodeEffectField<number>('specter_shared_passive_2', 'markDurationMs', 8000);
      const reduction = this.getNodeEffectField<number>('specter_shared_passive_2', 'markedAggroDecayReduction', 0.3);
      this.markRenderer.enable(duration, reduction);
    } else {
      this.markRenderer.disable();
    }
  }

  /** Register a single enemy with the StatusVFXOrchestrator for status-effect VFX tracking. */
  private registerEnemyStatusVfx(enemy: import('../entities/Enemy').Enemy): void {
    const tag = enemy.instanceTag;
    if (this.statusVfxRegisteredEnemies.has(tag)) return;
    this.statusVFXOrchestrator.registerEntity(
      tag,
      () => ({ x: enemy.sprite.x, y: enemy.sprite.y, depth: enemy.sprite.depth }),
      () => enemy.statusEffectTracker ?? null,
      () => ('setTint' in enemy.sprite ? enemy.sprite as Phaser.GameObjects.Sprite : null),
    );
    this.statusVfxRegisteredEnemies.add(tag);

    // Bridge tracker CC effects → AI state (e.g., stagger freezes the enemy)
    const tracker = enemy.statusEffectTracker;
    if (tracker) {
      tracker.onCCApplied = (defId: string, durationMs: number) => {
        if (defId === 'stagger') {
          enemy.ai.applyStagger(durationMs, false);
        }
      };
    }
  }

  /** Sync StatusVFXOrchestrator registrations with the current enemy list — register new, unregister dead. */
  private syncStatusVfxEntities(): void {
    const currentTags = new Set<string>();
    for (const enemy of this.enemyManager.all) {
      currentTags.add(enemy.instanceTag);
      this.registerEnemyStatusVfx(enemy);
    }
    // Unregister enemies that are no longer in the active list
    for (const tag of this.statusVfxRegisteredEnemies) {
      if (!currentTags.has(tag)) {
        this.statusVFXOrchestrator.unregisterEntity(tag);
        this.statusVfxRegisteredEnemies.delete(tag);
      }
    }
  }

  /** Enable simulation mode — suppresses ForkChoiceScene pauses, keeps the game loop running. */
  enableSimMode(): void {
    this._simMode = true;
  }

  /** Clear persistent world state (zones, summons, charges, terrain) for simulation reset between scenarios. */
  resetSimState(): void {
    this.zoneSystem.removeAll();
    this.summonSystem.destroyAll();
    this.chargeSystem.clear();
    this.terrainConstructionSystem.clear();
  }

  /** Active terrain count — exposed for SimulationBridge world state queries. */
  getTerrainActiveCount(): number {
    return this.terrainConstructionSystem.getActiveCount();
  }

  /** Stationary duration in ms — exposed for SimulationBridge debug queries. */
  getStationaryDurationMs(): number {
    return this.stationaryTracker.getStationaryDurationMs();
  }

  /** Force the stationary tracker to a given duration. Used by SimulationBridge for testing stationary-gated skills. */
  forceStationary(durationMs: number): void {
    this.stationaryTracker.forceStationary(durationMs);
  }
}
